import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../lib/apiClient';
import { useCustomers } from '../context/CustomerContext';
import { useAuth } from '../context/AuthContext';
import { EntityInvestigationPanel } from './EntityInvestigationPanel';
import { ThreatHuntAgentPanel } from './ThreatHuntAgentPanel';
import { KqlIntellisenseEditor } from './intelligence/KqlIntellisenseEditor';
import { AdvancedResultsGrid } from './AdvancedResultsGrid';
import { DEFENDER_XDR_TABLES_FOR_SENTINEL_WORKSPACE } from '../lib/kqlCatalog';
import {
  createReportPdf, drawSectionBanner, drawTable, drawCallout, drawEmptyState,
  drawClosingFooter, finalizeFooters, fmtGeneratedAt, savePdf, severityCell, statusCell,
  preloadPdfLogo, type TableCell,
} from '../lib/pdfTemplate';
import { getTz, fmtTs } from '../lib/globalTz';

interface Hunt {
  id: string;
  customerId: string;
  name: string;
  description?: string | null;
  kql: string;
  scheduleCron?: string | null;
  createdBy: string;
  lastRunAt?: string | null;
  lastRunRowCount?: string | null;
}

interface HuntOpsCatalogItem {
  id: string;
  sourceId: string;
  sourceName: string;
  sourcePath: string;
  sourceUrl?: string;
  name: string;
  description: string;
  category: string;
  severity: string;
  kql: string;
  requiredTables: string[];
  requiredParsers?: string[];
  asimParserGuidance?: string;
  tactics: string[];
  techniques: string[];
  tags: string[];
  queryHash: string;
  lastSyncedAt?: string;
  runnable: boolean;
  runnableStatus: 'runnable' | 'missing_tables' | 'blocked_by_policy' | 'unknown_data_readiness' | 'requires_asim_parser' | string;
  missingTables: string[];
  blockedTables: string[];
  deniedConstruct?: string;
  policyError?: string;
  readiness?: Array<{ table: string; present: boolean; lastSeen?: string; events?: number }>;
}

interface HuntOpsCatalogResponse {
  ok: boolean;
  catalog: HuntOpsCatalogItem[];
  summary?: {
    total: number;
    runnable: number;
    blockedByPolicy: number;
    missingTables: number;
    unknownDataReadiness: number;
    requiresAsimParser?: number;
    readinessStatus: string;
    readinessError?: string;
  };
  lastSync?: {
    imported?: number;
    source?: string;
    fallback?: boolean;
    githubAuthenticated?: boolean;
    syncMode?: string;
    localRepoPath?: string;
    errors?: string[];
    syncedAt?: string;
  } | null;
}

interface HuntOpsRunHistoryItem {
  id: string;
  customerId: string;
  catalogQueryId?: string;
  queryName: string;
  sourceId?: string;
  sourcePath?: string;
  queryText: string;
  requiredTables: string[];
  status: string;
  rowCount: number;
  durationMs?: number;
  resultPreview?: unknown[];
  columns: string[];
  errorMessage?: string;
  createdByEmail?: string;
  ranAt: string;
  metadata?: unknown;
  lookback?: string;
}

interface HuntOpsFinding {
  id: string;
  customerId: string;
  runId?: string;
  catalogQueryId?: string;
  queryName: string;
  title: string;
  severity: string;
  confidence: number;
  status: string;
  entityType?: string;
  entityValue?: string;
  tactics: string[];
  techniques: string[];
  evidence?: unknown;
  recommendedAction: string;
  aiSummary: string;
  createdByEmail?: string;
  createdAt: string;
  updatedAt: string;
  metadata?: unknown;
}

interface HuntRunResult {
  ok: boolean;
  runId: string;
  queryId?: string;
  queryName?: string;
  columns?: string[];
  rows?: unknown[][];
  rowCount: number;
  durationMs?: number;
  appliedLimit?: number;
  findingsCreated?: number;
  lookback?: string;
  result?: unknown;
}

type MissionType = 'identity_compromise' | 'around_incident' | 'around_critical_asset' | 'mitre_gaps' | 'fresh_threat_intel' | 'ransomware_precursor' | 'custom';

interface HuntPlanRecommendation {
  queryId: string;
  name: string;
  sourcePath: string;
  score: number;
  why: string;
  runnableStatus: string;
  requiredTables: string[];
  requiredParsers?: string[];
  tactics: string[];
  techniques: string[];
}

interface HuntPlanSkipped {
  queryId: string;
  name: string;
  sourcePath: string;
  runnableStatus: string;
  reason: string;
  requiredTables: string[];
  missingTables?: string[];
}

interface HuntPlan {
  missionType: MissionType;
  title: string;
  objective: string;
  prompt: string;
  generatedAt: string;
  readinessStatus?: string;
  readinessError?: string;
  aiNarrative: string;
  aiModel: string;
  aiFallback: boolean;
  recommended: HuntPlanRecommendation[];
  skipped: HuntPlanSkipped[];
  summary: { catalogQueriesEvaluated: number; recommended: number; skipped: number; requiresAsimParser: number };
}

interface HuntPlanResponse { ok: boolean; missionId: string; plan: HuntPlan; }
interface FindingsResponse { ok: boolean; findings: HuntOpsFinding[]; summary?: { total: number; new: number; approved: number; dismissed: number }; }


interface HuntOpsBookmark {
  id: string;
  customerId: string;
  title: string;
  bookmarkType: string;
  severity?: string;
  status: string;
  entityType?: string;
  entityValue?: string;
  queryName?: string;
  runId?: string;
  findingId?: string;
  evidence?: unknown;
  notes?: string;
  tags: string[];
  createdByEmail?: string;
  createdAt: string;
  updatedAt: string;
  metadata?: unknown;
}

interface BookmarksResponse { ok: boolean; bookmarks: HuntOpsBookmark[]; summary?: { total: number; open: number; investigating: number; closed: number }; }

interface HuntOpsSchedule {
  id: string;
  customerId: string;
  packType: string;
  title: string;
  description: string;
  cadence: string;
  lookback: string;
  enabled: boolean;
  maxQueries: number;
  status: string;
  lastRunAt?: string;
  nextRunAt?: string;
  lastResult?: unknown;
  createdByEmail?: string;
  createdAt: string;
  updatedAt: string;
  metadata?: unknown;
}

interface SchedulesResponse { ok: boolean; schedules: HuntOpsSchedule[]; summary?: { total: number; enabled: number; due: number }; }

export const huntsQueryKey = (customerId: string) => ['hunts', customerId] as const;
const catalogQueryKey = (customerId: string) => ['huntops-catalog', customerId] as const;
const runsQueryKey = (customerId: string) => ['huntops-runs', customerId] as const;
const findingsQueryKey = (customerId: string) => ['huntops-findings', customerId] as const;
const bookmarksQueryKey = (customerId: string) => ['huntops-bookmarks', customerId] as const;
const schedulesQueryKey = (customerId: string) => ['huntops-schedules', customerId] as const;

type ViewMode = 'planner' | 'agent' | 'catalog' | 'findings' | 'bookmarks' | 'schedules' | 'saved' | 'runs';

function statusBadge(status: string): string {
  switch (status) {
    case 'runnable': return 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 border-emerald-500/30';
    case 'requires_asim_parser': return 'bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30';
    case 'unknown_data_readiness': return 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30';
    case 'missing_tables': return 'bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/30';
    case 'blocked_by_policy': return 'bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/30';
    case 'asim_parser_missing': return 'bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30';
    case 'success': return 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 border-emerald-500/30';
    case 'failed': return 'bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/30';
    case 'approved': return 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 border-emerald-500/30';
    case 'dismissed': return 'bg-slate-500/15 text-slate-600 dark:text-slate-300 border-slate-500/30';
    case 'benign': return 'bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/30';
    case 'open': return 'bg-primary/10 text-primary border-primary/30';
    case 'investigating': return 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30';
    case 'closed': return 'bg-slate-500/15 text-slate-600 dark:text-slate-300 border-slate-500/30';
    case 'scheduled': return 'bg-primary/10 text-primary border-primary/30';
    case 'running': return 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30';
    case 'partial': return 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30';
    case 'new': return 'bg-primary/10 text-primary border-primary/30';
    default: return 'bg-muted text-muted-foreground border-border';
  }
}

function severityBadge(severity: string): string {
  const s = severity.toLowerCase();
  if (s === 'critical') return 'bg-red-600 text-white border-red-400/40';
  if (s === 'high') return 'bg-orange-500 text-white border-orange-300/40';
  if (s === 'medium') return 'bg-amber-500 text-slate-950 border-amber-300/40';
  return 'bg-sky-500 text-white border-sky-300/40';
}

function prettyStatus(status: string): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDate(value?: string | null): string {
  if (!value) return 'Never';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return String(value);
  // Client-selected timezone (globalTz), not the analyst's own browser locale.
  return fmtTs(dt);
}

function uniqueSorted(values: string[]): string[] {
  return Array.from(new Set(values.filter(Boolean))).sort((a, b) => a.localeCompare(b));
}

function downloadText(filename: string, content: string, type: string) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

async function exportHuntOpsPdf(input: {
  filenameBase: string;
  customerName: string;
  lookbackLabel: string;
  aiAnalysis: string | null;
  plan: HuntPlan | null;
  findings: HuntOpsFinding[];
  runs: HuntOpsRunHistoryItem[];
}) {
  await preloadPdfLogo();
  const ctx = createReportPdf({
    title: 'HuntOps Command Center',
    customerName: input.customerName,
    periodLabel: input.lookbackLabel,
    generatedLabel: fmtGeneratedAt(new Date().toISOString(), getTz()),
  });

  if (input.plan) {
    drawSectionBanner(ctx, 'Hunt Plan');
    drawCallout(ctx, { title: input.plan.title, body: input.plan.objective, tone: 'info' });
    drawCallout(ctx, {
      title: 'Plan Summary',
      body: `Recommended hunts: ${input.plan.recommended.length}  ·  Skipped: ${input.plan.skipped.length}  ·  Requires ASIM parser: ${input.plan.summary.requiresAsimParser}`,
      tone: 'neutral',
    });
  }

  if (input.aiAnalysis) {
    drawSectionBanner(ctx, 'AI Analysis');
    drawCallout(ctx, { title: 'Summary', body: input.aiAnalysis.slice(0, 3500), tone: 'info' });
  }

  drawSectionBanner(ctx, 'Findings Inbox');
  if (input.findings.length === 0) {
    drawEmptyState(ctx, 'No findings in the current inbox.');
  } else {
    const findingRows: TableCell[][] = input.findings.slice(0, 35).map((f) => [
      severityCell(f.severity),
      { content: `${f.confidence}%`, styles: { halign: 'center' } },
      statusCell(prettyStatus(f.status)),
      f.title,
      f.recommendedAction,
    ]);
    drawTable(ctx, {
      head: ['Severity', 'Confidence', 'Status', 'Title', 'Recommended Action'],
      body: findingRows,
      columnStyles: {
        0: { cellWidth: 20, halign: 'center' },
        1: { cellWidth: 20, halign: 'center' },
        2: { cellWidth: 22, halign: 'center' },
        3: { cellWidth: 55 },
        4: { cellWidth: 'auto' },
      },
      minRowHeight: 8,
    });
  }

  drawSectionBanner(ctx, 'Recent Hunt Runs');
  if (input.runs.length === 0) {
    drawEmptyState(ctx, 'No hunt runs recorded yet.');
  } else {
    const runRows: TableCell[][] = input.runs.slice(0, 35).map((r) => [
      formatDate(r.ranAt),
      statusCell(prettyStatus(r.status)),
      r.queryName,
      { content: String(r.rowCount), styles: { halign: 'center' } },
      r.errorMessage || '—',
    ]);
    drawTable(ctx, {
      head: ['Time', 'Status', 'Query', 'Rows', 'Error'],
      body: runRows,
      columnStyles: {
        0: { cellWidth: 34 },
        1: { cellWidth: 22, halign: 'center' },
        2: { cellWidth: 'auto' },
        3: { cellWidth: 16, halign: 'center' },
        4: { cellWidth: 40 },
      },
      minRowHeight: 8,
    });
  }

  drawClosingFooter(ctx);
  finalizeFooters(ctx);
  return savePdf(ctx, input.filenameBase);
}

const MISSION_OPTIONS: Array<{ value: MissionType; label: string; helper: string }> = [
  { value: 'identity_compromise', label: 'Hunt for identity compromise', helper: 'SigninLogs, AuditLogs, risky users, role changes, consent abuse.' },
  { value: 'around_incident', label: 'Hunt around this incident', helper: 'Alert/incident expansion and related entities.' },
  { value: 'around_critical_asset', label: 'Hunt around this critical asset', helper: 'Business-critical users, servers, IPs, URLs and cloud apps.' },
  { value: 'mitre_gaps', label: 'Hunt against MITRE gaps', helper: 'Prioritize ATT&CK-mapped coverage gaps.' },
  { value: 'fresh_threat_intel', label: 'Hunt using fresh threat intel', helper: 'Indicators, hashes, URLs, domains, IPs and TI tables.' },
  { value: 'ransomware_precursor', label: 'Hunt for ransomware precursors', helper: 'Process, file, network, lateral movement, backup tampering and impact signals.' },
  { value: 'custom', label: 'Custom hunt mission', helper: 'Use your own hunt objective.' },
];

const LOOKBACK_OPTIONS = [
  { value: '1h', label: 'Last 1 hour' },
  { value: '4h', label: 'Last 4 hours' },
  { value: '8h', label: 'Last 8 hours' },
  { value: '12h', label: 'Last 12 hours' },
  { value: '24h', label: 'Last 24 hours' },
  { value: '3d', label: 'Last 3 days' },
  { value: '7d', label: 'Last 7 days' },
  { value: '14d', label: 'Last 14 days' },
  { value: '30d', label: 'Last 30 days' },
];

const SCHEDULE_PRESETS = [
  { packType: 'daily_active_pack', title: 'Daily active hunt pack', cadence: 'daily', lookback: '24h', maxQueries: 5, description: 'Runs a safe daily set of high-value hunts across runnable catalog queries.' },
  { packType: 'weekly_mitre_gap_hunt', title: 'Weekly MITRE gap hunt', cadence: 'weekly', lookback: '7d', maxQueries: 6, description: 'Prioritizes ATT&CK-mapped hunts that improve weak or missing coverage.' },
  { packType: 'critical_asset_hunt', title: 'Critical asset hunt', cadence: 'daily', lookback: '24h', maxQueries: 5, description: 'Focuses on business-critical users, devices, servers, apps, IPs and URLs.' },
  { packType: 'identity_compromise_hunt', title: 'Identity compromise hunt', cadence: 'daily', lookback: '24h', maxQueries: 5, description: 'Looks for risky sign-ins, privilege changes, consent abuse and identity persistence.' },
  { packType: 'ransomware_precursor_hunt', title: 'Ransomware precursor hunt', cadence: 'daily', lookback: '24h', maxQueries: 5, description: 'Looks for pre-impact execution, lateral movement, backup tampering and mass-change signals.' },
];

export function HuntsTab() {
  const { activeCustomerId, activeCustomer } = useCustomers();
  const { canWrite, canCapability } = useAuth();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Partial<Hunt> | null>(null);
  const [results, setResults] = useState<HuntRunResult | null>(null);
  const [preview, setPreview] = useState<HuntOpsCatalogItem | null>(null);
  const [mode, setMode] = useState<ViewMode>('planner');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tableFilter, setTableFilter] = useState('all');
  const [tacticFilter, setTacticFilter] = useState('all');
  const [findingStatusFilter, setFindingStatusFilter] = useState('all');
  const [bookmarkStatusFilter, setBookmarkStatusFilter] = useState('all');
  const [selectedLookback, setSelectedLookback] = useState('24h');
  const [missionType, setMissionType] = useState<MissionType>('identity_compromise');
  const [missionPrompt, setMissionPrompt] = useState('');
  const [plan, setPlan] = useState<HuntPlan | null>(null);
  const [aiAnalysis, setAiAnalysis] = useState<string | null>(null);
  const [exportBusy, setExportBusy] = useState<string | null>(null);

  const readOnly = !canWrite('hunts', activeCustomerId);
  const canViewCatalog = canCapability('hunts', 'view_catalog');
  const canSyncCatalog = canCapability('hunts', 'sync_catalog');
  const canPreviewCatalog = canCapability('hunts', 'preview_catalog_query');
  const canRunCatalog = canCapability('hunts', 'run_catalog_query') && !readOnly;
  const canViewRuns = canCapability('hunts', 'view_run_history');
  const canGeneratePlan = canCapability('hunts', 'generate_plan');
  const canViewFindings = canCapability('hunts', 'view_findings');
  const canManageFindings = canCapability('hunts', 'manage_findings') && !readOnly;
  const canAiAnalysis = canCapability('hunts', 'ai_analysis');
  const canExport = canCapability('hunts', 'export');
  const canViewBookmarks = canCapability('hunts', 'view_bookmarks');
  const canManageBookmarks = canCapability('hunts', 'manage_bookmarks') && !readOnly;
  const canViewSchedules = canCapability('hunts', 'view_schedules');
  const canManageSchedules = canCapability('hunts', 'manage_schedules') && !readOnly;
  const canRunSchedules = canCapability('hunts', 'run_scheduled_hunts') && !readOnly;
  const canViewThreatHuntAgent = canCapability('hunts', 'view_threat_hunt_agent');
  const canRunThreatHuntAgent = canCapability('hunts', 'run_threat_hunt_agent') && !readOnly;
  const canViewThreatHuntCampaigns = canCapability('hunts', 'view_threat_hunt_campaigns');
  const canExportThreatHuntCampaigns = canCapability('hunts', 'export_threat_hunt_campaigns');
  const canPromoteThreatHuntFindings = canCapability('hunts', 'promote_threat_hunt_findings') && !readOnly;
  const canDeleteThreatHuntAgentHistory = canCapability('hunts', 'delete_threat_hunt_agent_history') && !readOnly;
  const canViewThreatHuntIntel = canCapability('hunts', 'view_threat_hunt_intel');
  const canRunThreatHuntIntel = canCapability('hunts', 'run_threat_hunt_intel') && !readOnly;
  const canExportThreatHuntIntel = canCapability('hunts', 'export_threat_hunt_intel');
  const canViewThreatHuntDiagnostics = canCapability('hunts', 'view_threat_hunt_diagnostics');
  const canRunThreatHuntDiagnostics = canCapability('hunts', 'run_threat_hunt_diagnostics') && !readOnly;
  const canViewAutoHuntScheduler = canCapability('hunts', 'view_auto_hunt_scheduler');
  const canRunAutoHuntNow = canCapability('hunts', 'run_auto_hunt_now') && !readOnly;
  const canManageAutoHuntScheduler = canCapability('hunts', 'manage_auto_hunt_scheduler') && !readOnly;
  const canViewScheduledIntelMonitoring = canCapability('hunts', 'view_scheduled_intel_monitoring');
  const canRunScheduledIntelMonitoring = canCapability('hunts', 'run_scheduled_intel_monitoring') && !readOnly;
  const canManageScheduledIntelMonitoring = canCapability('hunts', 'manage_scheduled_intel_monitoring') && !readOnly;
  const canViewBulkHuntExecution = canCapability('hunts', 'view_bulk_hunt_execution');
  const canRunBulkHuntExecution = canCapability('hunts', 'run_bulk_hunt_execution') && !readOnly;

  const huntsQuery = useQuery({
    queryKey: huntsQueryKey(activeCustomerId),
    queryFn: async () => {
      const data = await apiFetch<{ hunts: Hunt[] }>(`/hunts?customerId=${encodeURIComponent(activeCustomerId)}`);
      return data.hunts;
    },
    enabled: !!activeCustomerId,
  });

  const catalogQuery = useQuery({
    queryKey: catalogQueryKey(activeCustomerId),
    queryFn: async () => apiFetch<HuntOpsCatalogResponse>(`/hunts/catalog?customerId=${encodeURIComponent(activeCustomerId)}&limit=750`),
    enabled: !!activeCustomerId && canViewCatalog,
  });

  const runsQuery = useQuery({
    queryKey: runsQueryKey(activeCustomerId),
    queryFn: async () => {
      const data = await apiFetch<{ ok: boolean; runs: HuntOpsRunHistoryItem[] }>(`/hunts/runs?customerId=${encodeURIComponent(activeCustomerId)}&limit=150`);
      return data.runs ?? [];
    },
    enabled: !!activeCustomerId && canViewRuns,
  });

  const findingsQuery = useQuery({
    queryKey: findingsQueryKey(activeCustomerId),
    queryFn: async () => apiFetch<FindingsResponse>(`/hunts/findings?customerId=${encodeURIComponent(activeCustomerId)}&limit=150`),
    enabled: !!activeCustomerId && canViewFindings,
  });


  const bookmarksQuery = useQuery({
    queryKey: bookmarksQueryKey(activeCustomerId),
    queryFn: async () => apiFetch<BookmarksResponse>(`/hunts/bookmarks?customerId=${encodeURIComponent(activeCustomerId)}&limit=150`),
    enabled: !!activeCustomerId && canViewBookmarks,
  });

  const schedulesQuery = useQuery({
    queryKey: schedulesQueryKey(activeCustomerId),
    queryFn: async () => apiFetch<SchedulesResponse>(`/hunts/schedules?customerId=${encodeURIComponent(activeCustomerId)}`),
    enabled: !!activeCustomerId && canViewSchedules,
  });

  const hunts: Hunt[] = huntsQuery.data ?? [];
  const catalog = catalogQuery.data?.catalog ?? [];
  const runs = runsQuery.data ?? [];
  const findings = findingsQuery.data?.findings ?? [];
  const bookmarks = bookmarksQuery.data?.bookmarks ?? [];
  const schedules = schedulesQuery.data?.schedules ?? [];
  const displayError = error
    ?? (catalogQuery.error instanceof Error ? catalogQuery.error.message : null)
    ?? (runsQuery.error instanceof Error ? runsQuery.error.message : null)
    ?? (findingsQuery.error instanceof Error ? findingsQuery.error.message : null)
    ?? (bookmarksQuery.error instanceof Error ? bookmarksQuery.error.message : null)
    ?? (schedulesQuery.error instanceof Error ? schedulesQuery.error.message : null)
    ?? (huntsQuery.error instanceof Error ? huntsQuery.error.message : null);

  const filteredCatalog = useMemo(() => {
    const q = search.trim().toLowerCase();
    return catalog.filter((item) => {
      const haystack = [item.name, item.description, item.category, item.sourcePath, ...item.requiredTables, ...(item.requiredParsers ?? []), ...item.tactics, ...item.techniques, ...item.tags].join(' ').toLowerCase();
      if (q && !haystack.includes(q)) return false;
      if (statusFilter !== 'all' && item.runnableStatus !== statusFilter) return false;
      if (tableFilter !== 'all' && !item.requiredTables.includes(tableFilter)) return false;
      if (tacticFilter !== 'all' && !item.tactics.includes(tacticFilter)) return false;
      return true;
    });
  }, [catalog, search, statusFilter, tableFilter, tacticFilter]);

  const filteredFindings = useMemo(() => findings.filter((f) => findingStatusFilter === 'all' || f.status === findingStatusFilter), [findings, findingStatusFilter]);
  const filteredBookmarks = useMemo(() => bookmarks.filter((b) => bookmarkStatusFilter === 'all' || b.status === bookmarkStatusFilter), [bookmarks, bookmarkStatusFilter]);
  const allTables = useMemo(() => uniqueSorted(catalog.flatMap((item) => item.requiredTables)).slice(0, 200), [catalog]);
  const allTactics = useMemo(() => uniqueSorted(catalog.flatMap((item) => item.tactics)).slice(0, 100), [catalog]);

  const invalidateFor = (customerId: string) => {
    void queryClient.invalidateQueries({ queryKey: huntsQueryKey(customerId) });
    void queryClient.invalidateQueries({ queryKey: catalogQueryKey(customerId) });
    void queryClient.invalidateQueries({ queryKey: runsQueryKey(customerId) });
    void queryClient.invalidateQueries({ queryKey: findingsQueryKey(customerId) });
    void queryClient.invalidateQueries({ queryKey: bookmarksQueryKey(customerId) });
    void queryClient.invalidateQueries({ queryKey: schedulesQueryKey(customerId) });
  };

  const saveMutation = useMutation({
    mutationFn: async (vars: { customerId: string; hunt: Partial<Hunt> }) => {
      const e = vars.hunt;
      if (e.id) {
        return apiFetch(`/hunts/${e.id}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: e.name, description: e.description, kql: e.kql, scheduleCron: e.scheduleCron }),
        });
      }
      return apiFetch('/hunts', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId: vars.customerId, name: e.name, description: e.description, kql: e.kql, scheduleCron: e.scheduleCron }),
      });
    },
    onSuccess: (_d, vars) => { setEditing(null); invalidateFor(vars.customerId); },
    onError: (e) => setError(e instanceof Error ? e.message : 'Save failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: async (vars: { customerId: string; id: string }) => apiFetch(`/hunts/${vars.id}`, { method: 'DELETE' }),
    onSuccess: (_d, vars) => { invalidateFor(vars.customerId); },
    onError: (e) => setError(e instanceof Error ? e.message : 'Delete failed'),
  });

  const runSavedMutation = useMutation({
    mutationFn: async (vars: { customerId: string; id: string; lookback: string }) => apiFetch<HuntRunResult>(`/hunts/${vars.id}/run`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ lookback: vars.lookback }),
    }),
    onSuccess: (r, vars) => {
      setResults({ ...r, rows: Array.isArray((r.result as { rows?: unknown })?.rows) ? (r.result as { rows: unknown[][] }).rows : r.rows });
      invalidateFor(vars.customerId);
      if ((r.findingsCreated ?? 0) > 0) setMode('findings');
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Run failed'),
  });

  const syncMutation = useMutation({
    mutationFn: async () => apiFetch<{ ok: boolean; imported: number; source: string; fallback?: boolean; githubAuthenticated?: boolean; syncMode?: string; localRepoPath?: string; errors?: string[] }>('/hunts/catalog/sync', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customerId: activeCustomerId }),
    }),
    onSuccess: (r) => {
      if (r.fallback) setError(`HuntOps used the built-in fallback pack. ${r.errors?.[0] ?? ''}`);
      else setError(null);
      invalidateFor(activeCustomerId);
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Catalog sync failed'),
  });

  const planMutation = useMutation({
    mutationFn: async () => apiFetch<HuntPlanResponse>('/hunts/plan', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ customerId: activeCustomerId, missionType, prompt: missionPrompt }),
    }),
    onSuccess: (r) => { setPlan(r.plan); setError(null); setMode('planner'); },
    onError: (e) => setError(e instanceof Error ? e.message : 'Hunt plan generation failed'),
  });

  const runCatalogMutation = useMutation({
    mutationFn: async (vars: { item: { id: string; name: string }; lookback: string }) => apiFetch<HuntRunResult>(`/hunts/catalog/${encodeURIComponent(vars.item.id)}/run`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customerId: activeCustomerId, limit: 500, lookback: vars.lookback }),
    }),
    onSuccess: (r, vars) => {
      setResults({ ...r, queryId: vars.item.id, queryName: vars.item.name });
      invalidateFor(activeCustomerId);
      setMode((r.findingsCreated ?? 0) > 0 ? 'findings' : 'runs');
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Catalog hunt run failed'),
  });

  const findingStatusMutation = useMutation({
    mutationFn: async (vars: { id: string; status: string }) => apiFetch(`/hunts/findings/${encodeURIComponent(vars.id)}/status`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customerId: activeCustomerId, status: vars.status }),
    }),
    onSuccess: () => invalidateFor(activeCustomerId),
    onError: (e) => setError(e instanceof Error ? e.message : 'Finding update failed'),
  });

  const findingActionMutation = useMutation({
    mutationFn: async (vars: { id: string; action: string }) => apiFetch<{ ok: boolean; action: string; evidenceJson?: string; message?: string }>(`/hunts/findings/${encodeURIComponent(vars.id)}/action`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customerId: activeCustomerId, action: vars.action }),
    }),
    onSuccess: (r, vars) => {
      if (vars.action === 'export_evidence' && r.evidenceJson) downloadText(`huntops-finding-${vars.id}.json`, r.evidenceJson, 'application/json;charset=utf-8');
      else setError(r.message ?? null);
      invalidateFor(activeCustomerId);
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Finding action failed'),
  });


  const createBookmarkMutation = useMutation({
    mutationFn: async (vars: { findingId?: string; runId?: string; title?: string; notes?: string }) => apiFetch<{ ok: boolean; bookmark: HuntOpsBookmark }>('/hunts/bookmarks', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customerId: activeCustomerId, ...vars }),
    }),
    onSuccess: () => { invalidateFor(activeCustomerId); setMode('bookmarks'); setError(null); },
    onError: (e) => setError(e instanceof Error ? e.message : 'Bookmark create failed'),
  });

  const bookmarkStatusMutation = useMutation({
    mutationFn: async (vars: { id: string; status: string }) => apiFetch<{ ok: boolean; bookmark: HuntOpsBookmark }>(`/hunts/bookmarks/${encodeURIComponent(vars.id)}/status`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customerId: activeCustomerId, status: vars.status }),
    }),
    onSuccess: () => invalidateFor(activeCustomerId),
    onError: (e) => setError(e instanceof Error ? e.message : 'Bookmark update failed'),
  });

  const deleteBookmarkMutation = useMutation({
    mutationFn: async (vars: { id: string }) => apiFetch(`/hunts/bookmarks/${encodeURIComponent(vars.id)}?customerId=${encodeURIComponent(activeCustomerId)}`, { method: 'DELETE' }),
    onSuccess: () => invalidateFor(activeCustomerId),
    onError: (e) => setError(e instanceof Error ? e.message : 'Bookmark delete failed'),
  });

  const createScheduleMutation = useMutation({
    mutationFn: async (vars: { packType: string; cadence: string; lookback: string; maxQueries: number }) => apiFetch<{ ok: boolean; schedule: HuntOpsSchedule }>('/hunts/schedules', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customerId: activeCustomerId, enabled: true, ...vars }),
    }),
    onSuccess: () => { invalidateFor(activeCustomerId); setMode('schedules'); setError(null); },
    onError: (e) => setError(e instanceof Error ? e.message : 'Schedule create failed'),
  });

  const updateScheduleMutation = useMutation({
    mutationFn: async (vars: { id: string; enabled?: boolean; cadence?: string; lookback?: string; maxQueries?: number }) => apiFetch<{ ok: boolean; schedule: HuntOpsSchedule }>(`/hunts/schedules/${encodeURIComponent(vars.id)}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customerId: activeCustomerId, enabled: vars.enabled, cadence: vars.cadence, lookback: vars.lookback, maxQueries: vars.maxQueries }),
    }),
    onSuccess: () => invalidateFor(activeCustomerId),
    onError: (e) => setError(e instanceof Error ? e.message : 'Schedule update failed'),
  });

  const deleteScheduleMutation = useMutation({
    mutationFn: async (vars: { id: string }) => apiFetch(`/hunts/schedules/${encodeURIComponent(vars.id)}?customerId=${encodeURIComponent(activeCustomerId)}`, { method: 'DELETE' }),
    onSuccess: () => invalidateFor(activeCustomerId),
    onError: (e) => setError(e instanceof Error ? e.message : 'Schedule delete failed'),
  });

  const runScheduleMutation = useMutation({
    mutationFn: async (vars: { id: string }) => apiFetch(`/hunts/schedules/${encodeURIComponent(vars.id)}/run`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customerId: activeCustomerId }),
    }),
    onSuccess: () => { invalidateFor(activeCustomerId); setMode('runs'); },
    onError: (e) => setError(e instanceof Error ? e.message : 'Schedule run failed'),
  });

  const runDueSchedulesMutation = useMutation({
    mutationFn: async () => apiFetch('/hunts/schedules/run-due', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customerId: activeCustomerId, limit: 5 }),
    }),
    onSuccess: () => invalidateFor(activeCustomerId),
    onError: (e) => setError(e instanceof Error ? e.message : 'Run due schedules failed'),
  });

  const aiMutation = useMutation({
    mutationFn: async () => apiFetch<{ ok: boolean; analysis: string; model?: string; fallback?: boolean }>('/hunts/ai-analysis', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customerId: activeCustomerId, plan }),
    }),
    onSuccess: (r) => { setAiAnalysis(r.analysis); setError(null); },
    onError: (e) => setError(e instanceof Error ? e.message : 'AI analysis failed'),
  });

  async function exportData(format: 'csv' | 'pdf' | 'html') {
    if (!activeCustomerId) return;
    setExportBusy(format); setError(null);
    try {
      const parsed = await apiFetch<{ ok: boolean; csv?: string; html?: string; filenameBase?: string }>('/hunts/export', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customerId: activeCustomerId, format, aiAnalysis }),
      });
      const filenameBase = String(parsed.filenameBase || `kloudsecure-huntops-${activeCustomerId}`);
      if (format === 'csv') downloadText(`${filenameBase}.csv`, String(parsed.csv ?? ''), 'text/csv;charset=utf-8');
      if (format === 'html') downloadText(`${filenameBase}.html`, String(parsed.html ?? ''), 'text/html;charset=utf-8');
      if (format === 'pdf') await exportHuntOpsPdf({
        filenameBase,
        customerName: activeCustomer?.name ?? activeCustomerId ?? 'Customer',
        lookbackLabel: LOOKBACK_OPTIONS.find((o) => o.value === selectedLookback)?.label ?? selectedLookback,
        aiAnalysis,
        plan,
        findings,
        runs,
      });
    } catch (err) { setError(err instanceof Error ? err.message : String(err)); }
    finally { setExportBusy(null); }
  }

  function save(): void {
    if (!editing || !editing.name || !editing.kql) return;
    setError(null);
    saveMutation.mutate({ customerId: activeCustomerId, hunt: editing });
  }

  function remove(h: Hunt): void {
    if (!confirm(`Delete hunt "${h.name}"?`)) return;
    setError(null);
    deleteMutation.mutate({ customerId: h.customerId, id: h.id });
  }

  function runSaved(h: Hunt): void {
    setError(null); setResults(null); runSavedMutation.mutate({ customerId: h.customerId, id: h.id, lookback: selectedLookback });
  }

  function runCatalog(item: { id: string; name: string }): void {
    setError(null); setResults(null); runCatalogMutation.mutate({ item, lookback: selectedLookback });
  }

  async function loadCatalogDetails(item: HuntOpsCatalogItem): Promise<HuntOpsCatalogItem> {
    if (item.kql) return item;
    const data = await apiFetch<{ ok: boolean; query: HuntOpsCatalogItem }>(`/hunts/catalog/${encodeURIComponent(item.id)}/preview`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customerId: activeCustomerId }),
    });
    return data.query;
  }

  async function openPreview(item: HuntOpsCatalogItem): Promise<void> {
    try { setError(null); setPreview(await loadCatalogDetails(item)); }
    catch (e) { setError(e instanceof Error ? e.message : 'Preview failed'); }
  }

  async function saveCatalogAsHunt(item: HuntOpsCatalogItem): Promise<void> {
    try {
      const detailed = await loadCatalogDetails(item);
      setEditing({ name: detailed.name, description: detailed.description, kql: detailed.kql, scheduleCron: '' });
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not load KQL for saved copy'); }
  }

  const runningSaved = runSavedMutation.isPending ? runSavedMutation.variables?.id ?? null : null;
  const runningCatalog = runCatalogMutation.isPending ? runCatalogMutation.variables?.item.id ?? null : null;
  const summary = catalogQuery.data?.summary;
  const findingSummary = findingsQuery.data?.summary;
  const bookmarkSummary = bookmarksQuery.data?.summary;
  const scheduleSummary = schedulesQuery.data?.summary;
  const selectedMission = MISSION_OPTIONS.find((m) => m.value === missionType) ?? MISSION_OPTIONS[0]!;

  return (
    <div className="space-y-4 slide-in">
      <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-primary/80 font-bold">Active Threat Hunting</div>
            <h2 className="mt-1 text-xl font-bold text-foreground">HuntOps Command Center</h2>
            <p className="mt-1 max-w-4xl text-sm text-muted-foreground">
              AI-assisted active hunting with Microsoft Sentinel GitHub query catalog sync, local repo fallback, safe KQL validation, mission planning, ASIM parser awareness, manual execution and findings triage.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-2 rounded-lg border border-border bg-background px-2 py-1 text-xs text-muted-foreground">
              Lookback
              <select value={selectedLookback} onChange={(e) => setSelectedLookback(e.target.value)} className="bg-transparent text-xs font-semibold text-foreground outline-none">
                {LOOKBACK_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </label>
            <button onClick={() => void aiMutation.mutate()} disabled={!canAiAnalysis || aiMutation.isPending} className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold hover:bg-muted disabled:opacity-50">
              {aiMutation.isPending ? 'Analyzing…' : 'AI Analysis'}
            </button>
            <button onClick={() => void exportData('pdf')} disabled={!canExport || !!exportBusy} className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold hover:bg-muted disabled:opacity-50">Export PDF</button>
            <button onClick={() => void exportData('csv')} disabled={!canExport || !!exportBusy} className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold hover:bg-muted disabled:opacity-50">Export CSV</button>
            <button onClick={() => void catalogQuery.refetch()} disabled={catalogQuery.isFetching || !canViewCatalog} className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold hover:bg-muted disabled:opacity-50">
              {catalogQuery.isFetching ? 'Refreshing…' : 'Refresh Catalog'}
            </button>
            <button onClick={() => syncMutation.mutate()} disabled={!canSyncCatalog || syncMutation.isPending} title={!canSyncCatalog ? 'Your role cannot sync the HuntOps catalog.' : undefined} className="rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary/90 disabled:opacity-50">
              {syncMutation.isPending ? 'Syncing Catalog…' : 'Sync Hunt Catalog'}
            </button>
          </div>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-8">
          <div className="rounded-xl border border-border bg-muted/20 p-3"><div className="text-[11px] text-muted-foreground">Catalog Queries</div><div className="text-2xl font-black">{summary?.total ?? catalog.length}</div></div>
          <div className="rounded-xl border border-border bg-muted/20 p-3"><div className="text-[11px] text-muted-foreground">Runnable</div><div className="text-2xl font-black text-emerald-500">{summary?.runnable ?? 0}</div></div>
          <div className="rounded-xl border border-border bg-muted/20 p-3"><div className="text-[11px] text-muted-foreground">ASIM Parser Validation</div><div className="text-2xl font-black text-violet-500">{summary?.requiresAsimParser ?? 0}</div></div>
          <div className="rounded-xl border border-border bg-muted/20 p-3"><div className="text-[11px] text-muted-foreground">Missing Tables</div><div className="text-2xl font-black text-orange-500">{summary?.missingTables ?? 0}</div></div>
          <div className="rounded-xl border border-border bg-muted/20 p-3"><div className="text-[11px] text-muted-foreground">Findings</div><div className="text-2xl font-black">{findingSummary?.total ?? findings.length}</div></div>
          <div className="rounded-xl border border-border bg-muted/20 p-3"><div className="text-[11px] text-muted-foreground">Run History</div><div className="text-2xl font-black">{runs.length}</div></div>
          <div className="rounded-xl border border-border bg-muted/20 p-3"><div className="text-[11px] text-muted-foreground">Bookmarks</div><div className="text-2xl font-black">{bookmarkSummary?.total ?? bookmarks.length}</div></div>
          <div className="rounded-xl border border-border bg-muted/20 p-3"><div className="text-[11px] text-muted-foreground">Schedules</div><div className="text-2xl font-black">{scheduleSummary?.enabled ?? schedules.filter((s) => s.enabled).length}</div></div>
        </div>

        {catalogQuery.data?.lastSync && (
          <div className="mt-3 rounded-lg border border-border bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
            Last catalog sync: <span className="font-semibold text-foreground">{catalogQuery.data.lastSync.source ?? 'Unknown source'}</span>
            {catalogQuery.data.lastSync.syncedAt ? ` · ${formatDate(catalogQuery.data.lastSync.syncedAt)}` : ''}
            {catalogQuery.data.lastSync.syncMode === 'local_repo' ? ' · local VM mirror' : ''}
            {catalogQuery.data.lastSync.localRepoPath ? ` · ${catalogQuery.data.lastSync.localRepoPath}` : ''}
            {catalogQuery.data.lastSync.fallback ? ' · built-in fallback pack used' : ''}
            {catalogQuery.data.lastSync.syncMode === 'github_api' && catalogQuery.data.lastSync.githubAuthenticated === false ? ' · GitHub API unauthenticated' : ''}
            {catalogQuery.data.lastSync.syncMode === 'github_api' && catalogQuery.data.lastSync.githubAuthenticated === true ? ' · GitHub API authenticated' : ''}
          </div>
        )}
        {summary?.readinessError && <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">Table readiness warning: {summary.readinessError}</div>}
        {aiAnalysis && <div className="mt-3 rounded-lg border border-primary/20 bg-primary/10 px-3 py-2 text-xs text-foreground whitespace-pre-wrap"><span className="font-bold">AI Analysis:</span> {aiAnalysis}</div>}
        {displayError && <div className="mt-3 rounded-lg bg-red-500/20 px-3 py-2 text-xs text-red-700 dark:text-red-300 whitespace-pre-wrap">{displayError}</div>}
      </div>

      {!readOnly && <EntityInvestigationPanel customerId={activeCustomerId} />}

      <div className="flex flex-wrap gap-2">
        {([
          'planner',
          ...(canViewThreatHuntAgent ? ['agent' as ViewMode] : []),
          'catalog',
          'findings',
          'bookmarks',
          'schedules',
          'saved',
          'runs',
        ] as ViewMode[]).map((tab) => (
          <button key={tab} onClick={() => setMode(tab)} className={`rounded-lg border px-3 py-1.5 text-xs font-semibold ${mode === tab ? 'border-primary bg-primary text-white' : 'border-border hover:bg-muted'}`}>
            {tab === 'planner' ? 'AI Hunt Planner' : tab === 'agent' ? 'Threat Hunt Agent' : tab === 'catalog' ? 'Query Catalog' : tab === 'findings' ? 'Findings Inbox' : tab === 'bookmarks' ? 'Bookmarks' : tab === 'schedules' ? 'Schedules' : tab === 'saved' ? 'Saved Hunts' : 'Run History'}
          </button>
        ))}
      </div>

      {mode === 'agent' && canViewThreatHuntAgent && (
        <ThreatHuntAgentPanel
          customerId={activeCustomerId}
          canRun={canRunThreatHuntAgent}
          canViewCampaigns={canViewThreatHuntCampaigns}
          canExportCampaigns={canExportThreatHuntCampaigns}
          canPromoteFindings={canPromoteThreatHuntFindings}
          canDeleteHistory={canDeleteThreatHuntAgentHistory}
          canViewIntel={canViewThreatHuntIntel}
          canRunIntel={canRunThreatHuntIntel}
          canExportIntel={canExportThreatHuntIntel}
          canViewDiagnostics={canViewThreatHuntDiagnostics}
          canRunDiagnostics={canRunThreatHuntDiagnostics}
          canViewAutoHunt={canViewAutoHuntScheduler}
          canRunAutoHunt={canRunAutoHuntNow}
          canManageAutoHunt={canManageAutoHuntScheduler}
          canViewScheduledIntel={canViewScheduledIntelMonitoring}
          canRunScheduledIntel={canRunScheduledIntelMonitoring}
          canManageScheduledIntel={canManageScheduledIntelMonitoring}
          canViewBulkHunts={canViewBulkHuntExecution}
          canRunBulkHunts={canRunBulkHuntExecution}
        />
      )}

      {mode === 'planner' && (
        <div className="grid gap-4 xl:grid-cols-[420px_1fr]">
          <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
            <h3 className="text-sm font-bold text-foreground">AI Hunt Planner</h3>
            <p className="mt-1 text-xs text-muted-foreground">Generate an active hunt mission. AI selects safe catalog queries, explains why, and shows skipped hunts with reasons.</p>
            <div className="mt-4 space-y-3">
              <label className="block text-xs font-semibold text-muted-foreground">Mission type</label>
              <select value={missionType} onChange={(e) => setMissionType(e.target.value as MissionType)} className="input-default w-full text-xs">
                {MISSION_OPTIONS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
              <div className="rounded-lg border border-border bg-muted/20 p-3 text-xs text-muted-foreground">{selectedMission.helper}</div>
              <label className="block text-xs font-semibold text-muted-foreground">Additional hunt context</label>
              <textarea value={missionPrompt} onChange={(e) => setMissionPrompt(e.target.value)} rows={6} placeholder="Example: focus on domain controllers, VIP users, last 24h privilege changes, suspicious consent grants, or a specific incident/entity." className="input-default w-full text-xs" />
              <button onClick={() => planMutation.mutate()} disabled={!canGeneratePlan || planMutation.isPending} className="w-full rounded-lg bg-primary px-3 py-2 text-xs font-bold text-white hover:bg-primary/90 disabled:opacity-50">
                {planMutation.isPending ? 'Generating plan…' : 'Generate Hunt Plan'}
              </button>
              <div className="text-[11px] text-muted-foreground">Manual hunt runs from this plan will use the current global lookback: <span className="font-semibold text-foreground">{LOOKBACK_OPTIONS.find((o) => o.value === selectedLookback)?.label ?? selectedLookback}</span>.</div>
            </div>
          </div>
          <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
            {!plan ? (
              <div className="flex h-full min-h-[280px] items-center justify-center rounded-lg border border-dashed border-border text-center text-xs text-muted-foreground">Generate a mission to see recommended hunts, skipped hunts and AI rationale.</div>
            ) : (
              <div className="space-y-4">
                <div>
                  <div className="flex flex-wrap items-center gap-2"><h3 className="text-sm font-bold text-foreground">{plan.title}</h3><span className="rounded-full border border-border px-2 py-0.5 text-[10px] text-muted-foreground">{plan.aiModel}</span>{plan.aiFallback && <span className="rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[10px] text-amber-700 dark:text-amber-300">fallback</span>}</div>
                  <p className="mt-1 text-xs text-muted-foreground">{plan.objective}</p>
                  <div className="mt-2 rounded-lg border border-border bg-muted/20 p-3 text-xs whitespace-pre-wrap">{plan.aiNarrative}</div>
                </div>
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Recommended Hunts</h4>
                  <div className="mt-2 grid gap-2">
                    {plan.recommended.map((rec) => (
                      <div key={rec.queryId} className="rounded-lg border border-border bg-muted/10 p-3">
                        <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2"><span className="font-semibold text-foreground">{rec.name}</span><span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${statusBadge(rec.runnableStatus)}`}>{prettyStatus(rec.runnableStatus)}</span><span className="rounded-full border border-border px-2 py-0.5 text-[10px] text-muted-foreground">score {rec.score}</span></div>
                            <p className="mt-1 text-xs text-muted-foreground">{rec.why}</p>
                            <div className="mt-2 flex flex-wrap gap-1.5">{rec.requiredTables.slice(0, 8).map((t) => <span key={t} className="rounded border border-border bg-background px-2 py-0.5 font-mono text-[10px]">{t}</span>)}{(rec.requiredParsers ?? []).map((p) => <span key={p} className="rounded border border-violet-500/30 bg-violet-500/10 px-2 py-0.5 font-mono text-[10px] text-violet-700 dark:text-violet-300">{p}</span>)}</div>
                          </div>
                          <div className="flex shrink-0 gap-2"><button onClick={() => void openPreview(catalog.find((c) => c.id === rec.queryId) ?? ({ id: rec.queryId, name: rec.name } as HuntOpsCatalogItem))} className="rounded border border-border px-3 py-1 text-xs hover:bg-muted">Preview</button><button onClick={() => runCatalog({ id: rec.queryId, name: rec.name })} disabled={!canRunCatalog || runningCatalog === rec.queryId || rec.runnableStatus === 'blocked_by_policy' || rec.runnableStatus === 'missing_tables'} className="rounded bg-primary px-3 py-1 text-xs font-semibold text-white hover:bg-primary/90 disabled:opacity-50">{runningCatalog === rec.queryId ? 'Running…' : 'Run'}</button></div>
                        </div>
                      </div>
                    ))}
                    {plan.recommended.length === 0 && <div className="rounded border border-dashed border-border p-4 text-center text-xs text-muted-foreground">No safe recommended hunts for this mission yet. Sync the catalog or adjust filters/data sources.</div>}
                  </div>
                </div>
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Skipped / Not Runnable</h4>
                  <div className="mt-2 grid gap-2">
                    {plan.skipped.slice(0, 8).map((skip) => <div key={skip.queryId} className="rounded-lg border border-border bg-muted/10 p-3 text-xs"><div className="font-semibold text-foreground">{skip.name}</div><div className="mt-1 text-muted-foreground">{skip.reason}</div></div>)}
                    {plan.skipped.length === 0 && <div className="rounded border border-dashed border-border p-3 text-xs text-muted-foreground">No skipped hunts were returned for this mission.</div>}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {mode === 'catalog' && (
        <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div><h3 className="text-sm font-bold text-foreground">Microsoft Sentinel GitHub Hunt Catalog</h3><p className="text-xs text-muted-foreground">Safe read-only KQL only. ASIM parser-based queries are flagged separately because the parser pack may not exist in every workspace.</p></div>
            <div className="flex flex-wrap gap-2">
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, MITRE, table, parser, path…" className="input-default w-64 text-xs" />
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="input-default text-xs">
                <option value="all">All statuses</option><option value="runnable">Runnable</option><option value="requires_asim_parser">Requires ASIM parser</option><option value="unknown_data_readiness">Runnable with readiness warning</option><option value="missing_tables">Missing tables</option><option value="blocked_by_policy">Blocked by policy</option>
              </select>
              <select value={tableFilter} onChange={(e) => setTableFilter(e.target.value)} className="input-default text-xs"><option value="all">All tables</option>{allTables.map((t) => <option key={t} value={t}>{t}</option>)}</select>
              <select value={tacticFilter} onChange={(e) => setTacticFilter(e.target.value)} className="input-default text-xs"><option value="all">All tactics</option>{allTactics.map((t) => <option key={t} value={t}>{t}</option>)}</select>
            </div>
          </div>
          {catalogQuery.isLoading && <div className="mt-4 text-xs text-muted-foreground">Loading catalog…</div>}
          <div className="mt-4 grid gap-3">
            {filteredCatalog.map((item) => (
              <div key={item.id} className="rounded-xl border border-border bg-muted/10 p-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2"><h4 className="text-sm font-bold text-foreground">{item.name}</h4><span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${statusBadge(item.runnableStatus)}`}>{prettyStatus(item.runnableStatus)}</span>{item.severity && <span className="rounded-full border border-border px-2 py-0.5 text-[10px] text-muted-foreground">{item.severity}</span>}</div>
                    <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{item.description || 'No description supplied by the source repository.'}</p>
                    <div className="mt-2 flex flex-wrap gap-1.5">{item.requiredTables.slice(0, 8).map((table) => <span key={table} className="rounded border border-border bg-background px-2 py-0.5 text-[10px] font-mono">{table}</span>)}{(item.requiredParsers ?? []).map((parser) => <span key={parser} className="rounded border border-violet-500/30 bg-violet-500/10 px-2 py-0.5 text-[10px] font-mono text-violet-700 dark:text-violet-300">{parser}</span>)}{item.tactics.slice(0, 5).map((tactic) => <span key={tactic} className="rounded border border-primary/20 bg-primary/10 px-2 py-0.5 text-[10px] text-primary">{tactic}</span>)}{item.techniques.slice(0, 6).map((technique) => <span key={technique} className="rounded border border-border px-2 py-0.5 text-[10px] text-muted-foreground">{technique}</span>)}</div>
                    {(item.missingTables.length > 0 || item.blockedTables.length > 0 || item.policyError || item.asimParserGuidance) && <div className="mt-2 text-[11px] text-amber-700 dark:text-amber-300">{item.missingTables.length > 0 ? `Missing: ${item.missingTables.join(', ')}. ` : ''}{item.blockedTables.length > 0 ? `Blocked: ${item.blockedTables.join(', ')}. ` : ''}{item.policyError ? `${item.policyError}. ` : ''}{item.asimParserGuidance ? item.asimParserGuidance : ''}</div>}
                    <div className="mt-2 truncate text-[10px] text-muted-foreground">{item.sourceName} · {item.sourcePath}</div>
                  </div>
                  <div className="flex shrink-0 flex-wrap gap-2 lg:flex-col">
                    <button onClick={() => void openPreview(item)} disabled={!canPreviewCatalog} className="rounded border border-border px-3 py-1 text-xs hover:bg-muted disabled:opacity-50">Preview KQL</button>
                    <button onClick={() => runCatalog(item)} disabled={!canRunCatalog || runningCatalog === item.id || (!item.runnable && item.runnableStatus !== 'unknown_data_readiness' && item.runnableStatus !== 'requires_asim_parser')} title={!canRunCatalog ? 'Your role cannot run catalog hunts.' : undefined} className="rounded bg-primary px-3 py-1 text-xs font-semibold text-white hover:bg-primary/90 disabled:opacity-50">{runningCatalog === item.id ? 'Running…' : 'Run Hunt'}</button>
                    {!readOnly && <button onClick={() => void saveCatalogAsHunt(item)} className="rounded border border-border px-3 py-1 text-xs hover:bg-muted">Save Copy</button>}
                  </div>
                </div>
              </div>
            ))}
            {filteredCatalog.length === 0 && !catalogQuery.isLoading && <div className="py-8 text-center text-xs italic text-muted-foreground">No catalog queries matched the current filters.</div>}
          </div>
        </div>
      )}

      {mode === 'findings' && (
        <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div><h3 className="text-sm font-bold text-foreground">Findings Inbox</h3><p className="mt-1 text-xs text-muted-foreground">Raw hunt rows are converted into triageable findings with entity extraction, confidence, MITRE context and recommended actions.</p></div>
            <div className="flex gap-2"><select value={findingStatusFilter} onChange={(e) => setFindingStatusFilter(e.target.value)} className="input-default text-xs"><option value="all">All findings</option><option value="new">New</option><option value="approved">Approved</option><option value="dismissed">Dismissed</option><option value="benign">Benign</option></select><button onClick={() => void findingsQuery.refetch()} className="rounded border border-border px-3 py-1.5 text-xs hover:bg-muted">Refresh</button></div>
          </div>
          <div className="mt-4 grid gap-3">
            {filteredFindings.map((f) => (
              <div key={f.id} className="rounded-xl border border-border bg-muted/10 p-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2"><h4 className="text-sm font-bold text-foreground">{f.title}</h4><span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${severityBadge(f.severity)}`}>{f.severity}</span><span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${statusBadge(f.status)}`}>{prettyStatus(f.status)}</span><span className="rounded-full border border-border px-2 py-0.5 text-[10px] text-muted-foreground">confidence {f.confidence}%</span></div>
                    <div className="mt-1 text-xs text-muted-foreground">{f.entityType ?? 'entity'}: <span className="font-mono text-foreground">{f.entityValue ?? 'unmapped'}</span> · {formatDate(f.createdAt)} · {f.queryName}</div>
                    <p className="mt-2 text-xs text-foreground">{f.recommendedAction}</p>
                    <div className="mt-2 flex flex-wrap gap-1.5">{f.tactics.slice(0, 6).map((t) => <span key={t} className="rounded border border-primary/20 bg-primary/10 px-2 py-0.5 text-[10px] text-primary">{t}</span>)}{f.techniques.slice(0, 8).map((t) => <span key={t} className="rounded border border-border px-2 py-0.5 text-[10px] text-muted-foreground">{t}</span>)}</div>
                    {f.aiSummary && <div className="mt-2 text-[11px] text-muted-foreground">{f.aiSummary}</div>}
                  </div>
                  <div className="flex shrink-0 flex-wrap gap-2 lg:flex-col">
                    <button onClick={() => findingStatusMutation.mutate({ id: f.id, status: 'approved' })} disabled={!canManageFindings} className="rounded border border-emerald-500/40 px-3 py-1 text-xs text-emerald-600 hover:bg-emerald-500/10 disabled:opacity-50">Approve</button>
                    <button onClick={() => findingStatusMutation.mutate({ id: f.id, status: 'dismissed' })} disabled={!canManageFindings} className="rounded border border-border px-3 py-1 text-xs hover:bg-muted disabled:opacity-50">Dismiss</button>
                    <button onClick={() => findingStatusMutation.mutate({ id: f.id, status: 'benign' })} disabled={!canManageFindings} className="rounded border border-sky-500/40 px-3 py-1 text-xs text-sky-600 hover:bg-sky-500/10 disabled:opacity-50">Benign</button>
                    <button onClick={() => findingActionMutation.mutate({ id: f.id, action: 'add_to_case' })} disabled={!canManageFindings} className="rounded border border-border px-3 py-1 text-xs hover:bg-muted disabled:opacity-50">Add to Case</button>
                    <button onClick={() => findingActionMutation.mutate({ id: f.id, action: 'add_to_incident' })} disabled={!canManageFindings} className="rounded border border-border px-3 py-1 text-xs hover:bg-muted disabled:opacity-50">Add to Incident</button>
                    <button onClick={() => findingActionMutation.mutate({ id: f.id, action: 'add_to_jira' })} disabled={!canManageFindings} className="rounded border border-border px-3 py-1 text-xs hover:bg-muted disabled:opacity-50">Add to Jira</button>
                    <button onClick={() => createBookmarkMutation.mutate({ findingId: f.id, title: f.title })} disabled={!canManageBookmarks || createBookmarkMutation.isPending} className="rounded border border-primary/40 px-3 py-1 text-xs text-primary hover:bg-primary/10 disabled:opacity-50">Bookmark</button>
                    <button onClick={() => findingActionMutation.mutate({ id: f.id, action: 'export_evidence' })} disabled={!canManageFindings} className="rounded border border-border px-3 py-1 text-xs hover:bg-muted disabled:opacity-50">Export Evidence</button>
                  </div>
                </div>
              </div>
            ))}
            {filteredFindings.length === 0 && !findingsQuery.isLoading && <div className="py-8 text-center text-xs italic text-muted-foreground">No findings match the current filter. Run a hunt to generate findings.</div>}
          </div>
        </div>
      )}


      {mode === 'bookmarks' && (
        <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div><h3 className="text-sm font-bold text-foreground">Hunt Bookmarks</h3><p className="mt-1 text-xs text-muted-foreground">Sentinel-style evidence bookmarks for interesting hunt runs and findings. Use these as investigation pivots without turning every signal into an incident.</p></div>
            <div className="flex gap-2"><select value={bookmarkStatusFilter} onChange={(e) => setBookmarkStatusFilter(e.target.value)} className="input-default text-xs"><option value="all">All bookmarks</option><option value="open">Open</option><option value="investigating">Investigating</option><option value="closed">Closed</option></select><button onClick={() => void bookmarksQuery.refetch()} className="rounded border border-border px-3 py-1.5 text-xs hover:bg-muted">Refresh</button></div>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            <div className="rounded-lg border border-border bg-muted/20 p-3"><div className="text-[11px] text-muted-foreground">Open</div><div className="text-xl font-black">{bookmarkSummary?.open ?? bookmarks.filter((b) => b.status === 'open').length}</div></div>
            <div className="rounded-lg border border-border bg-muted/20 p-3"><div className="text-[11px] text-muted-foreground">Investigating</div><div className="text-xl font-black">{bookmarkSummary?.investigating ?? bookmarks.filter((b) => b.status === 'investigating').length}</div></div>
            <div className="rounded-lg border border-border bg-muted/20 p-3"><div className="text-[11px] text-muted-foreground">Closed</div><div className="text-xl font-black">{bookmarkSummary?.closed ?? bookmarks.filter((b) => b.status === 'closed').length}</div></div>
          </div>
          <div className="mt-4 grid gap-3">
            {filteredBookmarks.map((b) => (
              <div key={b.id} className="rounded-xl border border-border bg-muted/10 p-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2"><h4 className="text-sm font-bold text-foreground">{b.title}</h4><span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${statusBadge(b.status)}`}>{prettyStatus(b.status)}</span>{b.severity && <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${severityBadge(b.severity)}`}>{b.severity}</span>}</div>
                    <div className="mt-1 text-xs text-muted-foreground">{b.bookmarkType} · {b.queryName ?? 'No query'} · {formatDate(b.createdAt)} · by {b.createdByEmail ?? 'system'}</div>
                    {(b.entityType || b.entityValue) && <div className="mt-1 text-xs text-muted-foreground">Entity: <span className="font-mono text-foreground">{b.entityType ?? 'entity'}:{b.entityValue ?? 'unmapped'}</span></div>}
                    {b.notes && <p className="mt-2 text-xs text-foreground">{b.notes}</p>}
                    <pre className="mt-2 max-h-32 overflow-auto rounded border border-border bg-background p-2 font-mono text-[10px]">{JSON.stringify(b.evidence ?? {}, null, 2)}</pre>
                  </div>
                  <div className="flex shrink-0 flex-wrap gap-2 lg:flex-col">
                    <button onClick={() => bookmarkStatusMutation.mutate({ id: b.id, status: 'open' })} disabled={!canManageBookmarks || b.status === 'open'} className="rounded border border-border px-3 py-1 text-xs hover:bg-muted disabled:opacity-50">Open</button>
                    <button onClick={() => bookmarkStatusMutation.mutate({ id: b.id, status: 'investigating' })} disabled={!canManageBookmarks || b.status === 'investigating'} className="rounded border border-amber-500/40 px-3 py-1 text-xs text-amber-600 hover:bg-amber-500/10 disabled:opacity-50">Investigating</button>
                    <button onClick={() => bookmarkStatusMutation.mutate({ id: b.id, status: 'closed' })} disabled={!canManageBookmarks || b.status === 'closed'} className="rounded border border-border px-3 py-1 text-xs hover:bg-muted disabled:opacity-50">Close</button>
                    <button onClick={() => deleteBookmarkMutation.mutate({ id: b.id })} disabled={!canManageBookmarks} className="rounded border border-red-500/40 px-3 py-1 text-xs text-red-500 hover:bg-red-500/10 disabled:opacity-50">Delete</button>
                  </div>
                </div>
              </div>
            ))}
            {filteredBookmarks.length === 0 && !bookmarksQuery.isLoading && <div className="py-8 text-center text-xs italic text-muted-foreground">No bookmarks match the current filter. Add bookmarks from findings or run history.</div>}
          </div>
        </div>
      )}

      {mode === 'schedules' && (
        <div className="space-y-4">
          <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div><h3 className="text-sm font-bold text-foreground">Continuous Active Hunting Schedules</h3><p className="mt-1 text-xs text-muted-foreground">Controlled schedules run curated HuntOps packs with safe KQL validation, table readiness checks, row limits, lookback enforcement and run history.</p></div>
              <div className="flex flex-wrap gap-2"><button onClick={() => runDueSchedulesMutation.mutate()} disabled={!canRunSchedules || runDueSchedulesMutation.isPending} className="rounded border border-border px-3 py-1.5 text-xs hover:bg-muted disabled:opacity-50">{runDueSchedulesMutation.isPending ? 'Running due…' : 'Run Due Now'}</button><button onClick={() => void schedulesQuery.refetch()} className="rounded border border-border px-3 py-1.5 text-xs hover:bg-muted">Refresh</button></div>
            </div>
            <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-5">
              {SCHEDULE_PRESETS.map((preset) => (
                <div key={preset.packType} className="rounded-xl border border-border bg-muted/10 p-3">
                  <div className="text-sm font-bold text-foreground">{preset.title}</div>
                  <p className="mt-1 min-h-12 text-xs text-muted-foreground">{preset.description}</p>
                  <div className="mt-2 flex flex-wrap gap-1.5"><span className="rounded border border-border px-2 py-0.5 text-[10px]">{prettyStatus(preset.cadence)}</span><span className="rounded border border-border px-2 py-0.5 text-[10px]">{preset.lookback}</span><span className="rounded border border-border px-2 py-0.5 text-[10px]">max {preset.maxQueries}</span></div>
                  <button onClick={() => createScheduleMutation.mutate({ packType: preset.packType, cadence: preset.cadence, lookback: preset.lookback, maxQueries: preset.maxQueries })} disabled={!canManageSchedules || createScheduleMutation.isPending} className="mt-3 w-full rounded bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary/90 disabled:opacity-50">Add Schedule</button>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
            <div className="flex items-center justify-between"><div><h3 className="text-sm font-bold text-foreground">Configured Schedules</h3><p className="mt-1 text-xs text-muted-foreground">Enabled schedules are also checked by the backend worker. Set HUNTOPS_SCHEDULER_ENABLED=false to disable the worker.</p></div><div className="text-xs text-muted-foreground">{scheduleSummary?.enabled ?? 0} enabled · {scheduleSummary?.due ?? 0} due</div></div>
            <div className="mt-4 overflow-x-auto"><table className="min-w-full text-left text-xs"><thead className="text-[11px] uppercase tracking-wide text-muted-foreground"><tr><th className="px-2 py-2">Schedule</th><th className="px-2 py-2">Cadence</th><th className="px-2 py-2">Lookback</th><th className="px-2 py-2">Status</th><th className="px-2 py-2">Last / Next</th><th className="px-2 py-2">Last Result</th><th className="px-2 py-2">Actions</th></tr></thead><tbody>{schedules.map((sch) => { const last = sch.lastResult as { status?: string; selected?: number; success?: number; failed?: number; skippedReason?: string } | undefined; return <tr key={sch.id} className="border-t border-border align-top"><td className="px-2 py-2"><div className="font-semibold text-foreground">{sch.title}</div><div className="max-w-md text-[10px] text-muted-foreground">{sch.description}</div></td><td className="px-2 py-2">{prettyStatus(sch.cadence)}</td><td className="px-2 py-2">{sch.lookback}</td><td className="px-2 py-2"><span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${statusBadge(sch.enabled ? sch.status : 'closed')}`}>{sch.enabled ? prettyStatus(sch.status) : 'Disabled'}</span></td><td className="px-2 py-2 text-muted-foreground"><div>Last: {formatDate(sch.lastRunAt)}</div><div>Next: {formatDate(sch.nextRunAt)}</div></td><td className="px-2 py-2 text-muted-foreground">{last ? `${last.status ?? 'unknown'} · selected ${last.selected ?? 0} · success ${last.success ?? 0} · failed ${last.failed ?? 0}${last.skippedReason ? ` · ${last.skippedReason}` : ''}` : '—'}</td><td className="px-2 py-2"><div className="flex flex-wrap gap-1.5"><button onClick={() => runScheduleMutation.mutate({ id: sch.id })} disabled={!canRunSchedules || runScheduleMutation.isPending} className="rounded border border-border px-2 py-1 hover:bg-muted disabled:opacity-50">Run Now</button><button onClick={() => updateScheduleMutation.mutate({ id: sch.id, enabled: !sch.enabled })} disabled={!canManageSchedules} className="rounded border border-border px-2 py-1 hover:bg-muted disabled:opacity-50">{sch.enabled ? 'Disable' : 'Enable'}</button><button onClick={() => updateScheduleMutation.mutate({ id: sch.id, lookback: selectedLookback })} disabled={!canManageSchedules || sch.lookback === selectedLookback} className="rounded border border-border px-2 py-1 hover:bg-muted disabled:opacity-50">Use {selectedLookback}</button><button onClick={() => deleteScheduleMutation.mutate({ id: sch.id })} disabled={!canManageSchedules} className="rounded border border-red-500/40 px-2 py-1 text-red-500 hover:bg-red-500/10 disabled:opacity-50">Delete</button></div></td></tr>; })}</tbody></table>{schedules.length === 0 && !schedulesQuery.isLoading && <div className="py-8 text-center text-xs italic text-muted-foreground">No active hunting schedules configured yet.</div>}</div>
          </div>
        </div>
      )}

      {mode === 'saved' && (
        <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
          <div className="flex items-center justify-between"><div><h3 className="text-sm font-bold text-foreground">Saved KQL Hunts</h3><p className="mt-1 text-xs text-muted-foreground">Customer-scoped hunts created by analysts or saved from the HuntOps catalog.</p></div>{!readOnly && <button onClick={() => setEditing({ name: '', kql: '', description: '' })} className="rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary/90">+ New Hunt</button>}</div>
          {huntsQuery.isLoading && <div className="mt-3 text-xs text-muted-foreground">Loading saved hunts…</div>}
          <div className="mt-4 grid gap-3">
            {hunts.map((h) => <div key={h.id} className="rounded-lg border border-border bg-muted/20 p-3"><div className="flex items-start justify-between gap-3"><div className="min-w-0 flex-1"><div className="text-sm font-semibold text-foreground">{h.name}</div>{h.description && <div className="mt-0.5 text-xs text-muted-foreground">{h.description}</div>}<pre className="mt-2 max-h-32 overflow-x-auto whitespace-pre-wrap break-all rounded border border-border bg-background p-2 font-mono text-[11px]">{h.kql}</pre><div className="mt-1 text-[10px] text-muted-foreground">by {h.createdBy} · {h.lastRunAt ? `last run ${formatDate(h.lastRunAt)} (${h.lastRunRowCount ?? '?'} rows)` : 'never run'}</div></div><div className="flex shrink-0 flex-col gap-1.5"><button onClick={() => runSaved(h)} disabled={runningSaved === h.id || readOnly} className="rounded bg-primary px-3 py-1 text-xs font-semibold text-white hover:bg-primary/90 disabled:opacity-50">{runningSaved === h.id ? 'Running…' : 'Run'}</button>{!readOnly && <button onClick={() => setEditing(h)} className="rounded border border-border px-3 py-1 text-xs hover:bg-muted">Edit</button>}{!readOnly && <button onClick={() => remove(h)} className="rounded border border-red-500/40 px-3 py-1 text-xs text-red-500 hover:bg-red-500/10">Delete</button>}</div></div></div>)}
            {hunts.length === 0 && !huntsQuery.isLoading && <div className="py-6 text-center text-xs italic text-muted-foreground">No saved hunts yet.</div>}
          </div>
        </div>
      )}

      {mode === 'runs' && (
        <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
          <div className="flex items-center justify-between"><div><h3 className="text-sm font-bold text-foreground">Hunt Run History</h3><p className="mt-1 text-xs text-muted-foreground">Manual catalog and saved hunt executions are stored for audit, evidence review and repeatability.</p></div><button onClick={() => void runsQuery.refetch()} className="rounded border border-border px-3 py-1.5 text-xs hover:bg-muted">Refresh</button></div>
          <div className="mt-4 overflow-x-auto"><table className="min-w-full text-left text-xs"><thead className="text-[11px] uppercase tracking-wide text-muted-foreground"><tr><th className="px-2 py-2">Time</th><th className="px-2 py-2">Hunt</th><th className="px-2 py-2">Status</th><th className="px-2 py-2">Rows</th><th className="px-2 py-2">Duration</th><th className="px-2 py-2">Lookback</th><th className="px-2 py-2">User</th><th className="px-2 py-2">Details</th></tr></thead><tbody>{runs.map((run) => <tr key={run.id} className="border-t border-border align-top"><td className="px-2 py-2 text-muted-foreground">{formatDate(run.ranAt)}</td><td className="px-2 py-2"><div className="font-semibold text-foreground">{run.queryName}</div><div className="max-w-md truncate text-[10px] text-muted-foreground">{run.sourcePath}</div></td><td className="px-2 py-2"><span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${statusBadge(run.status)}`}>{prettyStatus(run.status)}</span></td><td className="px-2 py-2">{run.rowCount}</td><td className="px-2 py-2">{run.durationMs ? `${run.durationMs} ms` : '—'}</td><td className="px-2 py-2 text-muted-foreground">{(run.metadata as { lookback?: string } | undefined)?.lookback ?? run.lookback ?? '—'}</td><td className="px-2 py-2 text-muted-foreground">{run.createdByEmail ?? '—'}</td><td className="px-2 py-2"><div className="flex flex-wrap gap-1.5">{run.errorMessage ? <span className="whitespace-pre-wrap text-red-500">{run.errorMessage}</span> : <button onClick={() => setResults({ ok: true, runId: run.id, queryName: run.queryName, columns: run.columns, rows: run.resultPreview as unknown[][], rowCount: run.rowCount, durationMs: run.durationMs, lookback: (run.metadata as { lookback?: string } | undefined)?.lookback ?? run.lookback })} className="rounded border border-border px-2 py-1 hover:bg-muted">Preview</button>}<button onClick={() => createBookmarkMutation.mutate({ runId: run.id, title: `Bookmark: ${run.queryName}` })} disabled={!canManageBookmarks || createBookmarkMutation.isPending} className="rounded border border-primary/40 px-2 py-1 text-primary hover:bg-primary/10 disabled:opacity-50">Bookmark</button></div></td></tr>)}</tbody></table>{runs.length === 0 && !runsQuery.isLoading && <div className="py-8 text-center text-xs italic text-muted-foreground">No HuntOps runs recorded yet.</div>}</div>
        </div>
      )}

      {results && (
        <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
          <div className="mb-2 flex items-center justify-between"><h3 className="text-sm font-bold text-foreground">Run Results · {results.queryName ?? results.runId} · {results.rowCount} rows{results.findingsCreated !== undefined ? ` · ${results.findingsCreated} findings created` : ''}</h3><button onClick={() => setResults(null)} className="text-xs text-muted-foreground hover:text-foreground">Close</button></div>
          {results.columns && results.rows ? (
            <AdvancedResultsGrid
              columns={results.columns}
              rows={results.rows}
              storageKey={`hunts-run-results-${results.queryId ?? results.runId}`}
            />
          ) : (
            <pre className="max-h-96 overflow-auto rounded border border-border bg-muted/20 p-3 font-mono text-[11px]">{JSON.stringify({ columns: results.columns, rows: results.rows, rowCount: results.rowCount, durationMs: results.durationMs, appliedLimit: results.appliedLimit, lookback: results.lookback ?? selectedLookback, findingsCreated: results.findingsCreated }, null, 2)}</pre>
          )}
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setPreview(null)}>
          <div className="max-h-[90vh] w-full max-w-5xl overflow-hidden rounded-xl border border-border bg-card shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between border-b border-border p-4"><div><h3 className="text-sm font-bold text-foreground">{preview.name}</h3><p className="mt-1 text-xs text-muted-foreground">{preview.sourcePath}</p></div><button onClick={() => setPreview(null)} className="text-xs text-muted-foreground hover:text-foreground">Close</button></div>
            <div className="max-h-[70vh] overflow-auto p-4"><div className="mb-3 flex flex-wrap gap-1.5"><span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${statusBadge(preview.runnableStatus)}`}>{prettyStatus(preview.runnableStatus)}</span>{preview.requiredTables.map((table) => <span key={table} className="rounded border border-border bg-background px-2 py-0.5 font-mono text-[10px]">{table}</span>)}{(preview.requiredParsers ?? []).map((parser) => <span key={parser} className="rounded border border-violet-500/30 bg-violet-500/10 px-2 py-0.5 font-mono text-[10px] text-violet-700 dark:text-violet-300">{parser}</span>)}{preview.tactics.map((t) => <span key={t} className="rounded border border-primary/20 bg-primary/10 px-2 py-0.5 text-[10px] text-primary">{t}</span>)}</div>{preview.asimParserGuidance && <div className="mb-3 rounded border border-violet-500/30 bg-violet-500/10 p-2 text-xs text-violet-700 dark:text-violet-300">{preview.asimParserGuidance}</div>}<p className="mb-3 text-xs text-muted-foreground">{preview.description}</p><pre className="overflow-auto rounded border border-border bg-background p-3 font-mono text-[11px] whitespace-pre-wrap">{preview.kql}</pre></div>
            <div className="flex justify-end gap-2 border-t border-border p-4">{!readOnly && <button onClick={() => void saveCatalogAsHunt(preview)} className="rounded border border-border px-3 py-1.5 text-xs hover:bg-muted">Save Copy</button>}<button onClick={() => runCatalog(preview)} disabled={!canRunCatalog || (!preview.runnable && preview.runnableStatus !== 'unknown_data_readiness' && preview.runnableStatus !== 'requires_asim_parser')} className="rounded bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary/90 disabled:opacity-50">Run Hunt</button></div>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setEditing(null)}>
          <div className="w-full max-w-2xl space-y-3 rounded-xl border border-border bg-card p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-bold text-foreground">{editing.id ? 'Edit Saved Hunt' : 'New Saved Hunt'}</h3>
            <input className="input-default w-full" placeholder="Name" value={editing.name ?? ''} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
            <input className="input-default w-full" placeholder="Description (optional)" value={editing.description ?? ''} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
            <KqlIntellisenseEditor className="input-default w-full min-h-[220px] font-mono text-xs" source="sentinel" customerName={activeCustomer?.name} placeholder={'SecurityIncident\n| where TimeGenerated > ago(7d)\n| summarize count() by Severity'} value={editing.kql ?? ''} onChange={(v) => setEditing({ ...editing, kql: v })} schemaTables={DEFENDER_XDR_TABLES_FOR_SENTINEL_WORKSPACE} />
            <input className="input-default w-full font-mono" placeholder="Schedule (cron, optional. Not yet executed.)" value={editing.scheduleCron ?? ''} onChange={(e) => setEditing({ ...editing, scheduleCron: e.target.value })} />
            <div className="flex justify-end gap-2 pt-2"><button onClick={() => setEditing(null)} className="rounded border border-border px-3 py-1.5 text-xs hover:bg-muted">Cancel</button><button onClick={save} disabled={!editing.name || !editing.kql || saveMutation.isPending} className="rounded bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary/90 disabled:opacity-50">{saveMutation.isPending ? 'Saving…' : 'Save'}</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
